import * as React from 'react';
import * as _ from 'lodash';
import { IListItem, IContactForList, IContactEntityTypePositions, ICenterBase, IUser } from '@nkr/models/index';
import { IFilters } from '../filters';
import { GridApi, GridReadyEvent, FirstDataRenderedEvent } from 'ag-grid-community';
import { FormControl } from 'react-bootstrap';
import { MenuEvent } from '../menu-event';
import AdminFilters from './filters';
import AdminGrid from './grid';
import Delete from '../delete';
import HardDelete from '../hard-delete';
import BulkDelete from '../bulk-delete';

interface IProps {
    data: IContactForList[],
    centers: ICenterBase[],
    contactEntityTypes: IListItem[],
    roles: IContactEntityTypePositions[],
    types: IListItem[],
    filters: IFilters,
    showDeleted: boolean,
    currentUser: IUser,
    onSearch: () => void,
    onFilterChange: (filters: IFilters) => void,
    onShowDeletedChange: (showDeleted: boolean) => void,
    onDelete: (contactId: number) => void,
    onHardDelete: (contactId: number) => void,
    onHardDeleteBulk: (contactIds: number[]) => void,
    onExport: () => void,
    onOutlookExport: () => void,
}

interface IState {
    delete: {
        show: boolean,
        data: IContactForList
    },
    hardDelete: {
        show: boolean,
        data: IContactForList
    },
    bulkDelete: {
        show: boolean,
        selectedIds: number[]
    },
    selectedContactIds: number[],
    selectedForBulkDelete: number[],
    context: any,
    gridApi: GridApi
}

class AdminList extends React.Component<IProps, IState> {
    readonly state: IState = {
        delete: {
            show: false,
            data: null
        },
        hardDelete: {
            show: false,
            data: null
        },
        bulkDelete: {
            show: false,
            selectedIds: []
        },
        selectedContactIds: [],
        selectedForBulkDelete: [],
        context: { componentParent: this },
        gridApi: null
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
    }

    handleShowDeleted = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.onShowDeletedChange(e.currentTarget.value == '1');
    }

    handleReset = () => {
        let filters: IFilters = {
            centerId: 0,
            contactEntityTypeId: 0,
            search: '',
            centerRole: 0,
            adminRoles: []
        };

        this.props.onFilterChange(filters);
    }

    handleOutlookExport = () => {
        this.props.onOutlookExport();
    }

    handleExport = () => {
        this.props.onExport();
    }

    handleAdd = () => {
        window.location.href = '/contact/0';
    }

    handleMenuSelection = (eventKey: number, contactId: number) => {
        if (eventKey == MenuEvent.Edit) {
            window.location.href = `/contact/${contactId}`;
        }

        if (eventKey == MenuEvent.SoftDelete) {
            let contact = this.props.data.find(x => x.id == contactId);

            this.setState({
                delete: {
                    show: true,
                    data: contact
                }
            });
        }

        if (eventKey == MenuEvent.HardDelete) {
            let contact = this.props.data.find(x => x.id == contactId);

            this.setState({
                hardDelete: {
                    show: true,
                    data: contact
                }
            });
        }
    }

    handleCancelDelete = () => {
        this.setState({
            delete: {
                show: false,
                data: null
            }
        });
    }

    handleDeleteContact = () => {
        let contactId = this.state.delete.data.id;

        this.setState({
            delete: {
                show: false,
                data: null
            }
        }, () => {
            this.props.onDelete(contactId);
        });
    }

    handleHardDeleteContact = () => {
        let contactId = this.state.hardDelete.data.id;

        this.setState({
            hardDelete: {
                show: false,
                data: null
            }
        }, () => {
            this.props.onHardDelete(contactId);
        });
    }

    handleCancelHardDelete = () => {
        this.setState({
            hardDelete: {
                show: false,
                data: null
            }
        });
    }

    handleMainPageSelectionChange = (selectedIds: number[]) => {
        this.setState({
            selectedContactIds: selectedIds
        });
    }

    handleBulkDelete = () => {
        const selectedIds = this.state.selectedContactIds.length > 0 ? this.state.selectedContactIds : [];

        this.setState({
            bulkDelete: {
                show: true,
                selectedIds
            }
        });
    }

    handleCancelBulkDelete = () => {
        this.setState({
            bulkDelete: {
                show: false,
                selectedIds: []
            },
            selectedContactIds: []
        });
    }

    handleConfirmBulkDelete = () => {
        let selectedIds = this.state.bulkDelete.selectedIds;

        this.setState({
            bulkDelete: {
                show: false,
                selectedIds: []
            },
            selectedContactIds: []
        }, () => {
            this.props.onHardDeleteBulk(selectedIds);
        });
    }

    handleBulkSelectionChange = (selectedIds: number[]) => {
        this.setState({
            bulkDelete: {
                ...this.state.bulkDelete,
                selectedIds
            }
        });
    }

    handleSelectForBulkDelete = (contactId: number) => {
        let list = _.cloneDeep(this.state.selectedForBulkDelete);

        if (list.indexOf(contactId) >= 0) {
            list = list.filter(id => id != contactId);
        } else {
            list.push(contactId);
        }

        this.setState({
            selectedForBulkDelete: list
        });
    }

    getSelectedContactsForBulkDelete = () => {
        if (this.state.selectedContactIds.length > 0) {
            return this.props.data.filter(contact => this.state.selectedContactIds.includes(contact.id));
        }
        
        return this.props.data;
    }

    render = () => {
        
        return (
            <div style={{ paddingBottom: 10 }}>
                <AdminFilters
                    filters={this.props.filters}
                    centers={this.props.centers}
                    contactEntityTypes={this.props.contactEntityTypes}
                    roles={this.props.roles}
                    showDeleted={this.props.showDeleted}
                    canHardDelete={this.props.currentUser.canHardDelete}
                    onSearch={this.props.onSearch}
                    onChange={this.props.onFilterChange}
                    onShowDeletedChange={this.handleShowDeleted}
                    onAdd={this.handleAdd}
                    onExport={this.handleExport}
                    onOutlookExport={this.handleOutlookExport}
                    onReset={this.handleReset}
                    onBulkDelete={this.handleBulkDelete}
                    currentUser={this.props.currentUser}
                    contacts={this.props.data}
                />

                <AdminGrid
                    data={this.props.data}
                    canHardDelete={this.props.currentUser.canHardDelete}
                    selectedForBulkDelete={this.state.selectedForBulkDelete}
                    onGridReady={this.onGridReady}
                    onFirstDataRendered={this.onFirstDataRendered}
                    onMenuSelection={this.handleMenuSelection}
                    currentUser={this.props.currentUser}
                    contacts={this.props.data}
                    selectedContactIds={this.state.selectedContactIds}
                    onSelectionChange={this.handleMainPageSelectionChange}
                />

                <Delete
                    show={this.state.delete.show}
                    data={this.state.delete.data}
                    isCenter={false}
                    onDelete={this.handleDeleteContact}
                    onCancel={this.handleCancelDelete}
                />

                <HardDelete
                    show={this.state.hardDelete.show}
                    data={this.state.hardDelete.data}
                    isCenter={false}
                    onDelete={this.handleHardDeleteContact}
                    onCancel={this.handleCancelHardDelete}
                />

                <BulkDelete
                    show={this.state.bulkDelete.show}
                    data={this.getSelectedContactsForBulkDelete()}
                    selectedIds={this.state.bulkDelete.selectedIds}
                    onSelectionChange={this.handleBulkSelectionChange}
                    onDelete={this.handleConfirmBulkDelete}
                    onCancel={this.handleCancelBulkDelete}
                />
            </div>
        );
    }
}

export default AdminList;